/**
 * The routes (ui/DESIGN.md, "Information architecture and URLs"): Now, the stage, the Systems view with a system's
 * and an integration's page, a document as a page,
 * settings and login, plus the two wave-1 demos. The first UI's routes redirect: the result pages to the
 * stage with the query state it understands, the monitor to Now, a lens to its data model, a message to
 * its document page.
 */
import { Fragment, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { setNameMode, setNamesTechnical, technicalScope, useNameMode } from './lib/namemode.ts';
import { useSearchParams } from './lib/liveparams.ts';
import { ErrorPage } from './components/ErrorPage.tsx';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { useAuth } from './lib/auth.tsx';
import { Api, ApiError } from './lib/api.ts';
import { EnvProvider } from './lib/envs.tsx';
import { CatalogProvider } from './lib/catalogs.tsx';
import { SystemsProvider } from './lib/systemsdata.tsx';
import { useScrollRestoration } from './lib/scroll.ts';
import { Spinner } from './components/ui.tsx';
import { LoginPage } from './pages/Login.tsx';
import { Now } from './pages/Now.tsx';
import { Stage } from './pages/Stage.tsx';
import { SystemsView } from './pages/SystemsView.tsx';
import { SystemPage } from './pages/SystemPage.tsx';
import { IntegrationPage } from './pages/IntegrationPage.tsx';
import { DocumentPage } from './pages/Document.tsx';
import { Dev } from './dev/Dev.tsx';
import { ConnectorsSettings } from './pages/Connectors.tsx';
import { EnvironmentsSettings } from './pages/Environments.tsx';
import { VaultDetail } from './pages/VaultDetail.tsx';
import { ModelSettings, VaultsSettings, SettingsLayout, TenantsSettings, UsersSettings } from './pages/Settings.tsx';
import { SystemsSettings } from './pages/Systems.tsx';

function Loading() {
  return <div className="min-h-screen bg-n1 grid place-items-center"><Spinner /></div>;
}

/** Everything behind the login; a deep link keeps its URL and lands after signing in. */
function Protected() {
  const { me, loading, tenant } = useAuth();
  useScrollRestoration();
  if (loading) return <Loading />;
  if (!me) return <LoginPage />;
  if (!tenant) return <NoTenant />;
  return <EnvProvider tenantId={tenant.id}><CatalogProvider tenantId={tenant.id}><SystemsProvider tenantId={tenant.id}><NameScope><Outlet /></NameScope></SystemsProvider></CatalogProvider></EnvProvider>;
}

/** Technical names in the views of the data when the viewer chose them (lib/namemode.ts); remounts the page when it changes. */
function NameScope({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const pref = useNameMode();
  const inScope = technicalScope(pathname);
  // The URL says which names a view shows (so Back returns to it); a page reached without it gets the viewer's preference
  const urlMode = params.get('names');
  const mode = urlMode === 'technical' || urlMode === 'business' ? urlMode : pref;
  useEffect(() => {
    if (!inScope) return;
    if (urlMode === 'technical' || urlMode === 'business') { if (urlMode !== pref) setNameMode(urlMode); }
    else if (pref === 'technical') setParams((p) => { const out = new URLSearchParams(p); out.set('names', 'technical'); return out; }, { replace: true, state: window.history.state?.usr });
  }, [inScope, urlMode, pref]); // eslint-disable-line react-hooks/exhaustive-deps
  const technical = mode === 'technical' && inScope;
  setNamesTechnical(technical);
  return <Fragment key={technical ? 'technical' : 'business'}>{children}</Fragment>;
}

/** No tenant yet: a platform administrator creates the first one here (a fresh cloud has none), anyone else is told whom to ask. */
function NoTenant() {
  const { isAdmin, reload, setTenantId } = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(undefined);
    try {
      const t = await Api.createTenant(name.trim());
      setTenantId(t.id);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-n1 text-n9 grid place-items-center text-small p-6 text-center">
        No tenant available. Ask an administrator to add you to a tenant.
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-n1 text-n10 grid place-items-center p-6">
      <form onSubmit={create} className="w-full max-w-sm flex flex-col gap-3">
        <h1 className="text-title font-semibold">Create the first tenant</h1>
        <p className="text-small text-n8">A tenant is one customer's own MessageVault: its environments, vaults, users and data models. You can add more later in Settings.</p>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Tenant name, e.g. Informagic"
          className="h-10 px-3 rounded-lg bg-n2 border border-n4 text-body outline-none focus-visible:ring-2 focus-visible:ring-accent" />
        {error && <p className="text-small text-fail">{error}</p>}
        <button type="submit" disabled={busy || !name.trim()}
          className="h-10 rounded-lg bg-n10 text-n1 text-body font-medium disabled:opacity-50 cursor-pointer">{busy ? 'Creating…' : 'Create tenant'}</button>
      </form>
    </div>
  );
}

/** `/login` by its own URL: the page when logged out, Now when logged in. */
function LoginRoute() {
  const { me, loading } = useAuth();
  if (loading) return <Loading />;
  return me ? <Navigate to="/" replace /> : <LoginPage />;
}

/** The query state the stage understands; `step` keeps its encoding (one JSON step per param), the rest is dropped. */
const STAGE_PARAMS = ['q', 'step', 'range', 'from', 'to', 'asOf'];

/** The old result pages (/search, /data, /overview, /related) → the stage with their query. */
function ToStage() {
  const { search } = useLocation();
  const old = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const key of STAGE_PARAMS) for (const v of old.getAll(key)) next.append(key, v);
  const qs = next.toString();
  return <Navigate to={qs ? `/s?${qs}` : '/s'} replace />;
}

/** The old message page (/m/:vaultId/:uuid?day=) → the document's page, with its day. */
function ToDocument() {
  const { vaultId = '', uuid = '' } = useParams();
  const { search } = useLocation();
  const day = new URLSearchParams(search).get('day');
  return <Navigate to={`/d/${encodeURIComponent(vaultId)}/${encodeURIComponent(uuid)}${day ? `?day=${encodeURIComponent(day)}` : ''}`} replace />;
}

/** A lens of a virtual data model → the data model settings, with that model picked out. */
function ToModel() {
  const { id = '' } = useParams();
  return <Navigate to={id ? `/settings/model?model=${encodeURIComponent(id)}` : '/settings/model'} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginRoute /> },
  {
    path: '/',
    element: <Protected />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Now /> },
      { path: 's', element: <Stage /> },
      // The Systems view beside Now (docs/systems.md "Systems view")
      { path: 'systems', element: <SystemsView /> },
      { path: 'systems/:code', element: <SystemPage /> },
      { path: 'integrations/:process', element: <IntegrationPage /> },
      { path: 'd/:vaultId/:uuid', element: <DocumentPage /> },
      { path: 'dev/map', element: <Dev demo="map" /> },
      { path: 'dev/story', element: <Dev demo="story" /> },
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="/settings/vaults" replace /> },
          { path: 'vaults', element: <VaultsSettings /> },
          { path: 'vaults/:vid', element: <VaultDetail /> },
          { path: 'connectors', element: <ConnectorsSettings /> },
          { path: 'environments', element: <EnvironmentsSettings /> },
          { path: 'model', element: <ModelSettings /> },
          { path: 'systems', element: <SystemsSettings /> },
          { path: 'users', element: <UsersSettings /> },
          { path: 'tenants', element: <TenantsSettings /> },
        ],
      },
      // The first UI's routes
      { path: 'search', element: <ToStage /> },
      { path: 'data', element: <ToStage /> },
      { path: 'overview', element: <ToStage /> },
      { path: 'related', element: <ToStage /> },
      { path: 'monitor', element: <Navigate to="/" replace /> },
      { path: 'monitor/incident', element: <Navigate to="/" replace /> },
      { path: 'lens/:id', element: <ToModel /> },
      { path: 'm/:vaultId/:uuid', element: <ToDocument /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
